import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { IntentRing } from "@/components/IntentRing";
import { StatusBadge } from "@/components/StatusBadge";
import { BiblePanel } from "@/components/BiblePanel";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Download, Loader2, Play, Square, ShieldAlert, BookOpen, Languages, Pencil, Sparkles, BadgePercent, CheckCheck,
  Eye, ListOrdered, SpellCheck, ShieldCheck, Trash2, Quote, Feather, Users2, Tags, Wand2,
  Check, Circle, AlertTriangle, Settings2, SlidersHorizontal,
} from "lucide-react";

// ATLAS-EDITION-CARD (2026-10-13): split out of ProjectDetail.jsx. One primary
// action per state (translate / run pipeline), pipeline progress as a stage
// list, settings and single-stage buttons folded away.

const PREFACE_MODE_LABEL = {
  mechanical: "Mechanical",
  intro_only: "Intro edited",
  regenerated: "Regenerated",
};
const PREFACE_MODE_CLASS = {
  mechanical: "border-[#e6e4dd] bg-slate-50 text-slate-600",
  intro_only: "border-amber-200 bg-amber-50 text-amber-800",
  regenerated: "border-emerald-200 bg-emerald-50 text-emerald-800",
};

async function downloadFile(url, filename) {
  const { data } = await api.get(url, { responseType: "blob" });
  const href = URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = href; a.download = filename; document.body.appendChild(a); a.click();
  a.remove(); URL.revokeObjectURL(href);
}


// ATLAS-POLL-HOOK (2026-10-13): one shared poll loop for every stage on the
// card instead of a hand-written copy per stage. Calls step() every 3 s until
// it returns true, a request fails (onError), the card unmounts, or maxTries
// runs out (onTimeout).
function pollUntil(step, onError, onTimeout, maxTries = 400, aliveRef, intervalMs = 3000) {
  return (async () => {
    for (let i = 0; i < maxTries; i++) {
      await new Promise((r) => setTimeout(r, intervalMs));
      if (aliveRef && !aliveRef.current) return;
      try { if (await step()) return; }
      catch (err) { onError?.(err); return; }
    }
    onTimeout?.();
  })();
}

// Mirrors backend engine.QUALITY_PIPELINE_STAGES, grouped for display.
const PIPELINE_GROUPS = [
  ["Book bible & meaning review", ["bible_enforce", "names_check", "content_review"]],
  ["Grammar", ["grammar_check", "languagetool_check", "grammar_review", "languagetool_review", "apply_flags_1"]],
  ["Typography", ["typography_check", "apply_flags_2"]],
  ["Style & address", ["style_check", "address_check", "content_review_2", "apply_flags_3"]],
  ["Key terms", ["extract_key_terms", "term_consistency_check", "apply_flags_4"]],
  ["Translation memory", ["tm_build", "apply_flags_5"]],
  ["Titles & punctuation", ["normalize_titles", "fix_dangling_colons"]],
  ["Glossary", ["extract_glossary_candidates", "auto_glossary", "glossary_check"]],
  ["Resolve notes & final bible pass", ["resolve_notes", "bible_enforce_final"]],
];
const PIPELINE_ORDER = PIPELINE_GROUPS.flatMap(([, keys]) => keys);

function PipelineStages({ stage, status, errors }) {
  const cur = status === "done" ? PIPELINE_ORDER.length : PIPELINE_ORDER.indexOf(stage);
  return (
    <ol className="mt-2 space-y-0.5 text-[11px]" data-testid="pipeline-stages">
      {PIPELINE_GROUPS.map(([label, keys]) => {
        const first = PIPELINE_ORDER.indexOf(keys[0]);
        const last = PIPELINE_ORDER.indexOf(keys[keys.length - 1]);
        const failed = keys.filter((k) => errors && errors[k]);
        const state = cur > last ? "done" : (cur >= first ? "current" : "todo");
        return (
          <li key={label} className={`flex items-center gap-1.5 ${state === "todo" ? "text-[#94a3b8]" : "text-[#334155]"}`}
            title={failed.length ? failed.map((k) => `${k}: ${errors[k]}`).join("\n") : undefined}>
            {failed.length ? <AlertTriangle className="h-3 w-3 text-amber-600" />
              : state === "done" ? <Check className="h-3 w-3 text-emerald-600" />
              : state === "current" && status === "running" ? <Loader2 className="h-3 w-3 animate-spin" />
              : <Circle className={`h-3 w-3 ${state === "current" ? "text-amber-500" : ""}`} />}
            <span>{label}</span>
            {failed.length > 0 && <span className="text-amber-700">({failed.length} failed)</span>}
          </li>
        );
      })}
    </ol>
  );
}

export function EditionCard({ projectId, projectTitle, e, index, onChanged }) {
  const navigate = useNavigate();
  const aliveRef = useRef(true);
  useEffect(() => () => { aliveRef.current = false; }, []);
  const [busy, setBusy] = useState(false);
  const [runBusy, setRunBusy] = useState(false);
  const [publisherInput, setPublisherInput] = useState(e.publisher_name || "");
  const [prefaceBusy, setPrefaceBusy] = useState(null); // "rename" | "rewrite" | null
  const [applyAllBusy, setApplyAllBusy] = useState(false);
  const [titlesBusy, setTitlesBusy] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [stopBusy, setStopBusy] = useState(false);
  const [kdpBusy, setKdpBusy] = useState(false);
  const [reviewedBusy, setReviewedBusy] = useState(false);
  const [notesBusy, setNotesBusy] = useState(false);
  const [confirmNotes, setConfirmNotes] = useState(false);
  const dismissNotes = async () => {
    if (!confirmNotes) { setConfirmNotes(true); return; }
    setNotesBusy(true);
    try {
      const { data } = await api.post(`/editions/${e.id}/flags/dismiss-notes`);
      toast.success(`${data.dismissed} notes dismissed; they stay dismissed unless the passage changes`);
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not dismiss notes");
    } finally { setNotesBusy(false); setConfirmNotes(false); }
  };
  const flag = e.flag;
  const langInfo = e.lang_name;

  const total = e.passage_count || 0;
  const isTranslating = e.status === "translating";
  const isVerifying = e.status === "verifying";
  const pct = isTranslating ? (total ? Math.round((e.passages_done / total) * 100) : 0)
    : isVerifying ? (total ? Math.round((e.verify_done / total) * 100) : 0) : 0;

  const startEditionRun = async () => {
    setRunBusy(true);
    try {
      const { data } = await api.post(`/editions/${e.id}/run`);
      if (data.already_running) { toast.info("Already running"); }
      else { toast.success(`${langInfo} started`); }
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start this edition");
    } finally { setRunBusy(false); }
  };

  const download = async () => {
    setBusy(true);
    try { await downloadFile(`/editions/${e.id}/epub`, `${projectTitle}_${e.lang}.epub`); }
    catch (err) { toast.error("EPUB not ready"); }
    finally { setBusy(false); }
  };

  const toggleReviewed = async (checked) => {
    setReviewedBusy(true);
    try {
      await api.post(`/editions/${e.id}/reviewed`, { reviewed: checked });
      onChanged?.();
    } catch (err) {
      toast.error("Could not update reviewed status");
    } finally { setReviewedBusy(false); }
  };

  const toggleKdpPublished = async (checked) => {
    setKdpBusy(true);
    try {
      await api.post(`/editions/${e.id}/kdp-published`, { kdp_published: checked });
      onChanged?.();
    } catch (err) {
      toast.error("Could not update KDP status");
    } finally { setKdpBusy(false); }
  };

  const runPrefaceAction = async (kind) => {
    if (!publisherInput.trim()) { toast.error("Enter a publisher name first"); return; }
    setPrefaceBusy(kind);
    try {
      await api.post(`/editions/${e.id}/publisher/${kind}`, { publisher_name: publisherInput.trim() });
      toast.success(kind === "rename" ? "Publisher name updated" : "Preface regenerated");
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Preface edit failed");
    } finally { setPrefaceBusy(null); }
  };

  const applyAllFlags = async () => {
    setApplyAllBusy(true);
    try {
      const { data } = await api.post(`/editions/${e.id}/flags/apply-all`);
      if (data.skipped_preface > 0) {
        toast.success(`${data.applied} corrections applied — ${data.skipped_preface} were on the preface and won't show in the EPUB since a custom preface is active`);
      } else {
        toast.success(`${data.applied} corrections applied`);
      }
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not apply flags");
    } finally { setApplyAllBusy(false); }
  };

  const [gBusy, setGBusy] = useState(false);
  const [rvBusy, setRvBusy] = useState(false);
  const [rvRunning, setRvRunning] = useState(false);
  const [rvDone, setRvDone] = useState(e.grammar_review_done || 0);
  const [rvTotal, setRvTotal] = useState(0);
  const [gRunning, setGRunning] = useState(false);
  const [gDone, setGDone] = useState(e.grammar_done || 0);
  const gTotal = e.passage_count || 0;
  const gPct = gTotal ? Math.min(100, Math.round((gDone / gTotal) * 100)) : 0;

  const startGrammarCheck = async () => {
    setGBusy(true);
    try {
      const { data } = await api.post(`/editions/${e.id}/grammar/check`);
      if (data.already_done) { setGDone(data.grammar_done); toast.info("Grammar check already complete"); }
      else { setGRunning(true); toast.success("Grammar check started"); pollGrammar(); }
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not start grammar check"); }
    finally { setGBusy(false); }
  };

  const startGrammarReview = async () => {
    setRvBusy(true);
    try {
      const { data } = await api.post(`/editions/${e.id}/grammar/review`);
      if (data.already_running) { setRvRunning(true); pollReview(); }
      else { setRvRunning(true); toast.success("Grammar review started"); pollReview(); }
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not start grammar review"); }
    finally { setRvBusy(false); }
  };

  const pollReview = useCallback(() => pollUntil(async () => {
        const { data } = await api.get(`/editions/${e.id}/grammar/status`);
        setRvDone(data.review_done || 0);
        setRvTotal(data.review_total || 0);
        if (!data.review_running) { setRvRunning(false); onChanged?.(); return true; }
        return false;
      }, (err) => { setRvRunning(false); }, () => {
    setRvRunning(false);
  }, 500, aliveRef), [e.id]);
  const pollGrammar = useCallback(() => pollUntil(async () => {
        const { data } = await api.get(`/editions/${e.id}/grammar/status`);
        setGDone(data.grammar_done);
        if (!data.running) { setGRunning(false); onChanged?.(); return true; }
        return false;
      }, (err) => { setGRunning(false); onChanged?.(); }, () => {
    setGRunning(false);
  }, 400, aliveRef), [e.id]);

  const [tyBusy, setTyBusy] = useState(false);
  const [tyRunning, setTyRunning] = useState(false);
  const [tyDone, setTyDone] = useState(e.typo_dash_done || 0);
  const tyTotal = e.passage_count || 0;
  const tyPct = tyTotal ? Math.min(100, Math.round((tyDone / tyTotal) * 100)) : 0;
  const tyDoneAtAll = !!e.typo_review_done;

  const startTypographyCheck = async () => {
    setTyBusy(true);
    try {
      const { data } = await api.post(`/editions/${e.id}/typography/check`);
      if (data.already_running) { setTyRunning(true); pollTypography(); }
      else { setTyRunning(true); toast.success("Typography check started"); pollTypography(); }
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not start typography check"); }
    finally { setTyBusy(false); }
  };

  const pollTypography = useCallback(() => pollUntil(async () => {
        const { data } = await api.get(`/editions/${e.id}/typography/status`);
        setTyDone(data.typo_dash_done || 0);
        if (!data.running) {
          setTyRunning(false);
          if (data.review_done) {
            toast.success(`Typography check done — ${data.curled} quotes curled, ${data.dashes_fixed} dashes rewritten, ${data.new_flags} flagged for review`);
          }
          onChanged?.();
          return true;
        }
        return false;
      }, (err) => { setTyRunning(false); onChanged?.(); }, () => {
    setTyRunning(false);
  }, 400, aliveRef), [e.id]);

  const [stBusy, setStBusy] = useState(false);
  const [stRunning, setStRunning] = useState(false);
  const [stDone, setStDone] = useState(e.style_done || 0);
  const [stTotal, setStTotal] = useState(e.chapter_count || 0);
  const stPct = stTotal ? Math.min(100, Math.round((stDone / stTotal) * 100)) : 0;
  const stDoneAtAll = !!e.style_review_done;

  const startStyleCheck = async () => {
    setStBusy(true);
    try {
      const { data } = await api.post(`/editions/${e.id}/style/check`);
      setStTotal(data.total || 0);
      if (data.already_running) { setStRunning(true); pollStyle(); }
      else { setStRunning(true); toast.success("Style check started"); pollStyle(); }
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not start style check"); }
    finally { setStBusy(false); }
  };

  const pollStyle = useCallback(() => pollUntil(async () => {
        const { data } = await api.get(`/editions/${e.id}/style/status`);
        setStDone(data.style_done || 0);
        setStTotal(data.total || 0);
        if (!data.running) {
          setStRunning(false);
          if (data.review_done) { toast.success("Style check done — see flagged passages for title/structure/register issues"); }
          onChanged?.();
          return true;
        }
        return false;
      }, (err) => { setStRunning(false); onChanged?.(); }, () => {
    setStRunning(false);
  }, 400, aliveRef), [e.id]);

  const [adBusy, setAdBusy] = useState(false);
  const [adRunning, setAdRunning] = useState(false);
  const [adDone, setAdDone] = useState(e.address_done || 0);
  const [adTotal, setAdTotal] = useState(e.passage_count || 0);
  const adPct = adTotal ? Math.min(100, Math.round((adDone / adTotal) * 100)) : 0;
  const adDoneAtAll = !!e.address_review_done;
  // Only meaningful once the (cheap, per-chapter) style check has found a
  // majority address form to compare passages against.
  const adReady = !!e.address_ready;

  const startAddressCheck = async () => {
    setAdBusy(true);
    try {
      const { data } = await api.post(`/editions/${e.id}/address/check`);
      setAdTotal(data.total || 0);
      if (data.already_running) { setAdRunning(true); pollAddress(); }
      else { setAdRunning(true); toast.success("Address check started"); pollAddress(); }
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not start address check"); }
    finally { setAdBusy(false); }
  };

  const pollAddress = useCallback(() => pollUntil(async () => {
        const { data } = await api.get(`/editions/${e.id}/address/status`);
        setAdDone(data.address_done || 0);
        setAdTotal(data.total || 0);
        if (!data.running) {
          setAdRunning(false);
          if (data.review_done) { toast.success("Address check done — see flagged passages for isolated tu/usted-type slips"); }
          onChanged?.();
          return true;
        }
        return false;
      }, (err) => { setAdRunning(false); onChanged?.(); }, () => {
    setAdRunning(false);
  }, 400, aliveRef), [e.id]);

  const [termBusy, setTermBusy] = useState(false);
  const [termRunning, setTermRunning] = useState(false);
  const [termDoneAtAll, setTermDoneAtAll] = useState(!!e.terms_last_run);
  const [termFlaggedNew, setTermFlaggedNew] = useState(e.terms_flagged_new || 0);

  const startTermCheck = async () => {
    setTermBusy(true);
    try {
      const { data } = await api.post(`/editions/${e.id}/terms/check`);
      if (data.already_running) { setTermRunning(true); pollTerms(); }
      else { setTermRunning(true); toast.success("Term consistency check started"); pollTerms(); }
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not start term consistency check"); }
    finally { setTermBusy(false); }
  };

  const pollTerms = useCallback(() => pollUntil(async () => {
        const { data } = await api.get(`/editions/${e.id}/terms/status`);
        if (!data.running) {
          setTermRunning(false);
          setTermDoneAtAll(true);
          setTermFlaggedNew(data.flagged_new || 0);
          toast.success(
            data.flagged_new > 0
              ? `Term check done — ${data.flagged_new} inconsistent key-term rendering(s) flagged`
              : "Term check done — no key-term inconsistencies found"
          );
          onChanged?.();
          return true;
        }
        return false;
      }, (err) => { setTermRunning(false); onChanged?.(); }, () => {
    setTermRunning(false);
  }, 400, aliveRef), [e.id]);

  // --- Book glossary (deterministic term-normalisation pass) ---
  // ATLAS-AUTO-GLOSSARY-FRONTEND: single automatic action, no human confirmation step --
  // extract, pick each term's clear-majority rendering from real usage
  // across the edition (same per-occurrence evidence term_consistency_check
  // uses), confirm, and sweep, all in one click. Built because the person
  // running Atlas does not read the target languages, so typing a
  // "canonical translation" per term was never actually reviewable by them.
  const [glDone, setGlDone] = useState(!!e.glossary_done);
  const [glBusy, setGlBusy] = useState(false);
  const [glRunning, setGlRunning] = useState(false);
  const [glResult, setGlResult] = useState(null);

  const pollGlossary = useCallback(() => pollUntil(async () => {
        const { data } = await api.get(`/editions/${e.id}/glossary/status`);
        if (!data.running) {
          setGlRunning(false);
          setGlDone(!!data.done);
          setGlResult({
            normalized: data.normalized_count || 0,
            terms: data.replaced_terms || 0,
            flagged: data.flagged_count || 0,
            confirmedTerms: (data.terms || []).length,
          });
          toast.success(
            `Glossary done — ${(data.terms || []).length} term(s) confirmed from usage, ` +
            `${data.normalized_count || 0} passage(s) normalised` +
            (data.flagged_count > 0 ? `, ${data.flagged_count} flagged for review` : ", nothing ambiguous")
          );
          onChanged?.();
          return true;
        }
        return false;
      }, (err) => { setGlRunning(false); onChanged?.(); }, () => {
    setGlRunning(false);
  }, 400, aliveRef, 2000), [e.id]);

  const startGlossaryAuto = async () => {
    setGlBusy(true);
    try {
      const { data } = await api.post(`/editions/${e.id}/glossary/auto`);
      setGlRunning(true);
      if (!data.already_running) toast.success("Glossary check started — picking canonical terms from usage, no input needed");
      pollGlossary();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not start glossary check"); }
    finally { setGlBusy(false); }
  };

  const glLabel = glDone ? "Re-check & normalise terms" : "Check & normalise terms";

  // --- Translation memory (etapa 2): repeated-source consistency ---
  // Single-action button per the plan: build groups identical sources, flags
  // the differing renderings; the hook in the translate stage reuses validated
  // entries on future runs of this edition.
  const [tmBusy, setTmBusy] = useState(false);
  const [tmRunning, setTmRunning] = useState(false);
  const [tmBuilt, setTmBuilt] = useState(!!e.tm_built_at);
  const [tmInfo, setTmInfo] = useState({
    repeated: e.tm_repeated_groups || 0,
    inconsistent: e.tm_inconsistent_groups || 0,
    flagged: e.tm_flagged || 0,
    validated: !!e.tm_validated,
  });

  const pollTm = useCallback(() => pollUntil(async () => {
        const { data } = await api.get(`/editions/${e.id}/tm/status`);
        if (!data.running) {
          setTmRunning(false);
          setTmBuilt(!!data.built_at);
          setTmInfo({
            repeated: data.repeated_groups || 0,
            inconsistent: data.inconsistent_groups || 0,
            flagged: data.flagged || 0,
            validated: !!data.validated,
          });
          toast.success(
            `TM built — ${data.repeated_groups || 0} repeated source group(s)` +
            (data.inconsistent_groups > 0
              ? `, ${data.inconsistent_groups} inconsistent (${data.flagged} flagged)`
              : ", all consistent") +
            (data.validated ? "" : " — not validated yet: finish grammar + typography first")
          );
          onChanged?.();
          return true;
        }
        return false;
      }, (err) => { setTmRunning(false); onChanged?.(); }, () => {
    setTmRunning(false);
  }, 200, aliveRef, 1500), [e.id]);

  const startTmBuild = async () => {
    setTmBusy(true);
    try {
      await api.post(`/editions/${e.id}/tm/build`);
      setTmRunning(true);
      toast.success("Translation memory build started");
      pollTm();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not start TM build"); }
    finally { setTmBusy(false); }
  };

  const normalizeTitles = async () => {
    setTitlesBusy(true);
    try {
      await api.post(`/editions/${e.id}/titles/normalize`);
      toast.success("Chapter titles unified to one consistent style");
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not normalize titles");
    } finally { setTitlesBusy(false); }
  };

  const stopEdition = async () => {
    setStopBusy(true);
    try {
      const { data } = await api.post(`/editions/${e.id}/stop`);
      toast.success(data.mode === "forced" ? `${langInfo} reset (was stuck)` : `${langInfo} stopping…`);
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not stop this edition");
    } finally { setStopBusy(false); }
  };

  const deleteEdition = async () => {
    if (!confirmDelete) { setConfirmDelete(true); return; }
    setDeleteBusy(true);
    try {
      await api.delete(`/editions/${e.id}`);
      toast.success(`${langInfo} edition deleted`);
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete this edition");
      setConfirmDelete(false);
    } finally { setDeleteBusy(false); }
  };

  // ATLAS-QUALITY-PIPELINE-BUTTON: single orchestrated "clean edition" run, replaces
  // clicking grammar/typography/style/address/term/tm/titles by hand.
  const [pipelineBusy, setPipelineBusy] = useState(false);
  const [pipelineStatus, setPipelineStatus] = useState(e.quality_pipeline?.status || null);
  const [pipelineStage, setPipelineStage] = useState(e.quality_pipeline?.stage || null);
  const pipelineRunning = pipelineStatus === "running";
  const PIPELINE_STAGE_LABEL = {
    bible_enforce: "Applying the book bible", bible_enforce_final: "Re-applying the book bible",
    resolve_notes: "Resolving notes", names_check: "Checking names", languagetool_review: "LanguageTool review", content_review: "Reviewing meaning flags", content_review_2: "Reviewing style flags", languagetool_check: "LanguageTool check",
    grammar_check: "Grammar check", grammar_review: "Grammar review", apply_flags_1: "Applying corrections",
    typography_check: "Typography check", apply_flags_2: "Applying corrections",
    style_check: "Style check", address_check: "Address check", apply_flags_3: "Applying corrections",
    extract_key_terms: "Extracting key terms", term_consistency_check: "Term consistency check", apply_flags_4: "Applying corrections",
    tm_build: "Building translation memory", apply_flags_5: "Applying corrections",
    normalize_titles: "Normalizing chapter titles",
    fix_dangling_colons: "Fixing punctuation",
    extract_glossary_candidates: "Extracting glossary candidates",
    auto_glossary: "Picking canonical terms from usage",
    glossary_check: "Normalising glossary terms",
    queued: "Queued", done: "Done",
  };

  const pollPipeline = useCallback(() => pollUntil(async () => {
        const { data } = await api.get(`/editions/${e.id}/pipeline/status`);
        const qp = data.pipeline || {};
        setPipelineStage(qp.stage || null);
        setPipelineStatus(qp.status || null);
        if (qp.status !== "running") {
          if (qp.status === "done") {
            const errCount = Object.keys(qp.errors || {}).length;
            toast.success(errCount
              ? `Pipeline done — ${errCount} stage(s) hit an error, check flags/logs`
              : "Pipeline done — edition cleaned up, only the glossary step (if any) is left for you to confirm");
          } else if (qp.status === "stopped") {
            toast.success("Pipeline stopped");
          } else if (qp.status === "interrupted") {
            toast.error("Pipeline interrupted — click again, it resumes from where it stopped");
          }
          onChanged?.();
          return true;
        }
        return false;
      }, (err) => { setPipelineStatus(null); }, () => {

  }, 1500, aliveRef, 4000), [e.id]);

  // ATLAS-PIPELINE-STOP
  const [pipelineStopBusy, setPipelineStopBusy] = useState(false);
  const stopPipeline = async () => {
    setPipelineStopBusy(true);
    try {
      await api.post(`/editions/${e.id}/pipeline/stop`);
      setPipelineStatus("stopped");
      toast.success("Pipeline stopped");
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not stop the pipeline");
    } finally { setPipelineStopBusy(false); }
  };

  const startPipeline = async () => {
    setPipelineBusy(true);
    try {
      const { data } = await api.post(`/editions/${e.id}/pipeline/run`);
      setPipelineStatus("running");
      if (!data.already_running) toast.success("Full pipeline started — grammar through chapter titles, unattended");
      pollPipeline();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start the pipeline");
    } finally { setPipelineBusy(false); }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: index * 0.05 }}
      data-testid={`edition-card-${e.lang}`}
      className="rounded-2xl border border-[#e6e4dd] bg-white p-5 transition-shadow hover:shadow-md">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="font-display text-xl tracking-tight">{langInfo}</h3>
          <span className="text-xs uppercase tracking-[0.15em] text-[#94a3b8] font-mono2">{e.lang}</span>
        </div>
        <IntentRing score={e.fidelity_score} size={60} label={false} />
      </div>

      <div className="mt-4 flex items-center gap-2">
        <StatusBadge status={e.status} />
        {["translating", "verifying", "queued", "grammar_check"].includes(e.status) && (
          <Button data-testid={`stop-edition-${e.lang}`} size="sm" variant="outline" disabled={stopBusy}
            onClick={stopEdition}
            title="Stop just this edition"
            className="ml-auto h-6 w-6 rounded-full border-[#e6e4dd] p-0 hover:bg-rose-600 hover:text-white">
            {stopBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Square className="h-3.5 w-3.5" />}
          </Button>
        )}
        {!["translating", "verifying", "queued", "grammar_check"].includes(e.status) && (
          <Button data-testid={`delete-edition-${e.lang}`} size="sm" variant="outline" disabled={deleteBusy}
            onClick={deleteEdition}
            onBlur={() => setConfirmDelete(false)}
            title={confirmDelete ? "Click again to confirm deletion" : "Delete this edition"}
            className={`ml-auto h-6 w-6 rounded-full border-[#e6e4dd] p-0 ${confirmDelete ? "bg-rose-600 text-white hover:bg-rose-700" : "hover:bg-rose-600 hover:text-white"}`}>
            {deleteBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
          </Button>
        )}
      </div>

      {(isTranslating || isVerifying) && (
        <div className="mt-4">
          <div className="mb-1 flex justify-between text-xs text-[#475569]">
            <span>{isTranslating ? "Translating passages" : "Verifying intent"}</span>
            <span className="tabnums">{pct}%</span>
          </div>
          <Progress value={pct} className="h-1.5" />
        </div>
      )}

      {e.status === "error" && <p className="mt-3 text-xs text-rose-600">{e.error}</p>}

      {["pending", "paused", "stopped", "error"].includes(e.status) && (
        <Button data-testid={`edition-run-main-${e.lang}`} size="sm" disabled={runBusy} onClick={startEditionRun}
          className="mt-3 h-9 w-full rounded-lg bg-slate-900 px-2 text-xs text-white hover:bg-slate-700 disabled:opacity-60">
          {runBusy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Play className="mr-1.5 h-3.5 w-3.5" />}
          {e.status === "error" ? "Translate missing passages" : (e.status === "pending" ? "Translate" : "Resume translation")}
        </Button>
      )}

        {gTotal > 0 && !isTranslating && !isVerifying && (
          <div className="mt-4">
            <Button data-testid={`pipeline-run-${e.lang}`} size="sm" disabled={pipelineBusy || pipelineRunning}
              onClick={startPipeline}
              title="Runs every QC stage in order (grammar, typography, style, address, key terms, translation memory, chapter titles), auto-applying every trustworthy correction along the way. Resumable — safe to click again if interrupted."
              className="h-9 w-full rounded-lg bg-slate-900 px-2 text-xs text-white hover:bg-slate-700 disabled:opacity-60">
              {pipelineBusy || pipelineRunning ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Wand2 className="mr-1.5 h-3.5 w-3.5" />}
              {pipelineRunning
                ? `Running pipeline — ${PIPELINE_STAGE_LABEL[pipelineStage] || pipelineStage || "starting"}…`
                : (pipelineStatus === "done" || pipelineStatus === "stopped" ? "Re-run full pipeline" : "Run full pipeline")}
            </Button>
            {pipelineRunning && (
              <Button data-testid={`pipeline-stop-${e.lang}`} size="sm" variant="outline" disabled={pipelineStopBusy}
                onClick={stopPipeline}
                className="mt-2 h-8 w-full rounded-lg border-red-200 px-2 text-xs text-red-700 hover:bg-red-50 disabled:opacity-60">
                {pipelineStopBusy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Square className="mr-1.5 h-3.5 w-3.5" />}
                Stop pipeline
              </Button>
            )}
            {pipelineStatus && (
              <PipelineStages stage={pipelineStage} status={pipelineStatus} errors={e.quality_pipeline?.errors} />
            )}
          </div>
        )}
      <details className="group mt-3 rounded-xl border border-[#e6e4dd] bg-[#faf9f6]" data-testid={`edition-settings-${e.lang}`}>
        <summary className="flex cursor-pointer list-none items-center justify-between px-3 py-2 text-xs font-medium text-[#475569]">
          <span className="inline-flex items-center gap-1.5"><Settings2 className="h-3.5 w-3.5" /> Edition settings</span>
          <span className="truncate pl-2 text-[11px] font-normal text-[#94a3b8]">{e.publisher_name || ""}</span>
        </summary>
        <div className="border-t border-[#e6e4dd] p-3">
        <div className="flex flex-wrap items-center gap-1.5 text-xs">
          <span className={`inline-flex items-center rounded-full border px-2 py-0.5 font-medium ${PREFACE_MODE_CLASS[e.preface_mode] || PREFACE_MODE_CLASS.mechanical}`}>
            {PREFACE_MODE_LABEL[e.preface_mode] || "Mechanical"}
          </span>
          {typeof e.mt_factor === "number" && (
            <span className="inline-flex items-center gap-1 rounded-full border border-[#e6e4dd] bg-white px-2 py-0.5 text-[#475569]">
              <BadgePercent className="h-3 w-3" /> MT {Math.round(e.mt_factor * 100)}%
            </span>
          )}
          {e.publisher_name && <span className="text-[#94a3b8]">{e.publisher_name}</span>}
        </div>
        <div className="mt-2 flex items-center gap-1.5">
          <Input value={publisherInput} onChange={(ev) => setPublisherInput(ev.target.value)}
            placeholder="Publisher name" data-testid={`publisher-input-${e.lang}`}
            className="h-8 flex-1 rounded-lg border-[#e6e4dd] bg-white text-xs" />
          <Button data-testid={`publisher-rename-${e.lang}`} size="sm" variant="outline" disabled={!!prefaceBusy}
            onClick={() => runPrefaceAction("rename")}
            title="Minimal edit: swap only the publisher name in the existing preface"
            className="h-8 rounded-lg border-[#e6e4dd] px-2 text-xs hover:bg-slate-900 hover:text-white">
            {prefaceBusy === "rename" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Pencil className="h-3.5 w-3.5" />}
          </Button>
          <Button data-testid={`publisher-rewrite-${e.lang}`} size="sm" variant="outline" disabled={!!prefaceBusy}
            onClick={() => runPrefaceAction("rewrite")}
            title="Regenerate the whole preface from scratch in this publisher's voice"
            className="h-8 rounded-lg border-[#e6e4dd] px-2 text-xs hover:bg-slate-900 hover:text-white">
            {prefaceBusy === "rewrite" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
          </Button>
        </div>
          <BiblePanel edition={e} onChanged={onChanged} />
        </div>
      </details>
      {gTotal > 0 && !isTranslating && !isVerifying && (
      <details className="group mt-2 rounded-xl border border-[#e6e4dd] bg-[#faf9f6]" data-testid={`edition-manual-${e.lang}`}>
        <summary className="flex cursor-pointer list-none items-center justify-between px-3 py-2 text-xs font-medium text-[#475569]">
          <span className="inline-flex items-center gap-1.5"><SlidersHorizontal className="h-3.5 w-3.5" /> Manual stages</span>
          <span className="text-[11px] font-normal text-[#94a3b8]">run one step on its own</span>
        </summary>
        <div className="border-t border-[#e6e4dd] p-3">
        {gTotal > 0 && !isTranslating && !isVerifying && (
          <div className="mt-2">
            <Button data-testid={`grammar-check-${e.lang}`} size="sm" variant="outline"
              disabled={gBusy || gRunning}
              onClick={startGrammarCheck}
              title="Run the grammar guardian pass: checks every passage's grammar in the target language and flags genuine mistakes"
              className="h-8 w-full rounded-lg border-[#e6e4dd] px-2 text-xs hover:bg-slate-900 hover:text-white disabled:opacity-60">
              {gBusy || gRunning ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <SpellCheck className="mr-1.5 h-3.5 w-3.5" />}
              {gRunning ? `Grammar check ${gPct}%` : (gDone >= gTotal && gTotal > 0 ? "Re-run grammar check" : "Grammar check")}
            </Button>
            {(gRunning || (gDone > 0 && gDone < gTotal)) && (
              <Progress value={gPct} className="mt-1 h-1" />
            )}
          </div>
        )}
        {e.grammar_done >= gTotal && gTotal > 0 && !isTranslating && !isVerifying && (
          <div className="mt-2">
            <Button data-testid={`grammar-review-${e.lang}`} size="sm" variant="outline"
              disabled={rvBusy || rvRunning}
              onClick={startGrammarReview}
              title="Second-opinion review: the model judges every open grammar flag and applies the safe verdicts automatically"
              className="h-8 w-full rounded-lg border-[#e6e4dd] px-2 text-xs hover:bg-slate-900 hover:text-white disabled:opacity-60">
              {rvBusy || rvRunning ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="mr-1.5 h-3.5 w-3.5" />}
              {rvRunning ? `Grammar review ${rvTotal ? Math.round((rvDone / rvTotal) * 100) : 0}%` : (rvDone ? `Grammar review (${rvDone} done)` : "Grammar review")}
            </Button>
            {(rvRunning || (rvDone > 0 && rvDone < rvTotal)) && (
              <Progress value={rvTotal ? Math.round((rvDone / rvTotal) * 100) : 0} className="mt-1 h-1" />
            )}
          </div>
        )}
        {e.grammar_done >= gTotal && gTotal > 0 && !isTranslating && !isVerifying && (
          <div className="mt-2">
            <Button data-testid={`typography-check-${e.lang}`} size="sm" variant="outline"
              disabled={tyBusy || tyRunning}
              onClick={startTypographyCheck}
              title="Post-grammar finishing pass: curls quotes/apostrophes to the target language's own convention, rewrites away stray em/en-dashes, and flags translation-artifact corruption for review"
              className="h-8 w-full rounded-lg border-[#e6e4dd] px-2 text-xs hover:bg-slate-900 hover:text-white disabled:opacity-60">
              {tyBusy || tyRunning ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Quote className="mr-1.5 h-3.5 w-3.5" />}
              {tyRunning ? `Typography check ${tyPct}%` : (tyDoneAtAll ? "Re-run typography check" : "Run typography check")}
            </Button>
            {(tyRunning || (tyDone > 0 && tyDone < tyTotal)) && (
              <Progress value={tyPct} className="mt-1 h-1" />
            )}
          </div>
        )}
        {e.grammar_done >= gTotal && gTotal > 0 && !isTranslating && !isVerifying && (
          <div className="mt-2">
            <Button data-testid={`style-check-${e.lang}`} size="sm" variant="outline"
              disabled={stBusy || stRunning}
              onClick={startStyleCheck}
              title="Different-angle pass: checks whether every chapter title was actually translated, whether stanza/line structure matches the original, and whether any chapter's register (archaic vs modern) drifts from the rest of the book"
              className="h-8 w-full rounded-lg border-[#e6e4dd] px-2 text-xs hover:bg-slate-900 hover:text-white disabled:opacity-60">
              {stBusy || stRunning ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Feather className="mr-1.5 h-3.5 w-3.5" />}
              {stRunning ? `Style check ${stPct}%` : (stDoneAtAll ? "Re-run style check" : "Run style check")}
            </Button>
            {(stRunning || (stDone > 0 && stDone < stTotal)) && (
              <Progress value={stPct} className="mt-1 h-1" />
            )}
          </div>
        )}
        {adReady && !isTranslating && !isVerifying && (
          <div className="mt-2">
            <Button data-testid={`address-check-${e.lang}`} size="sm" variant="outline"
              disabled={adBusy || adRunning}
              onClick={startAddressCheck}
              title="Passage-level pass: re-checks every passage individually against the book's own majority address form (tu/usted, du/Sie, tu/vous-type) and flags isolated slips the chapter-level style check's coarser majority missed"
              className="h-8 w-full rounded-lg border-[#e6e4dd] px-2 text-xs hover:bg-slate-900 hover:text-white disabled:opacity-60">
              {adBusy || adRunning ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Users2 className="mr-1.5 h-3.5 w-3.5" />}
              {adRunning ? `Address check ${adPct}%` : (adDoneAtAll ? "Re-run address check" : "Run address check (per passage)")}
            </Button>
            {(adRunning || (adDone > 0 && adDone < adTotal)) && (
              <Progress value={adPct} className="mt-1 h-1" />
            )}
          </div>
        )}
        {e.grammar_done >= gTotal && gTotal > 0 && !isTranslating && !isVerifying && (
          <div className="mt-2">
            <Button data-testid={`term-check-${e.lang}`} size="sm" variant="outline"
              disabled={termBusy || termRunning}
              onClick={startTermCheck}
              title="Checks the book's own recurring key terms/concepts (e.g. a philosophical or technical term used dozens of times) for consistent rendering across this edition, and proposes a fix for any passage that drifts from this edition's own majority wording"
              className="h-8 w-full rounded-lg border-[#e6e4dd] px-2 text-xs hover:bg-slate-900 hover:text-white disabled:opacity-60">
              {termBusy || termRunning ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Tags className="mr-1.5 h-3.5 w-3.5" />}
              {termRunning ? "Checking key terms…" : (termDoneAtAll ? "Re-run term check" : "Run key-term consistency check")}
            </Button>
          </div>
        )}
        {e.grammar_done >= gTotal && gTotal > 0 && !isTranslating && !isVerifying && (
          <div className="mt-2">
            <Button data-testid={`glossary-check-${e.lang}`} size="sm" variant="outline"
              disabled={glBusy || glRunning}
              onClick={startGlossaryAuto}
              title="Book glossary, fully automatic: extracts the book's own recurring terms from the source manuscript, picks each term's canonical rendering from how this edition already renders it most often (same evidence-based method as the key-term consistency check, no manual translation needed), then normalises every occurrence with zero extra LLM calls. Genuinely ambiguous terms are left flagged for review instead of guessed."
              className="h-8 w-full rounded-lg border-[#e6e4dd] px-2 text-xs hover:bg-slate-900 hover:text-white disabled:opacity-60">
              {glBusy || glRunning ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <BookOpen className="mr-1.5 h-3.5 w-3.5" />}
              {glRunning ? "Checking glossary…" : glLabel}
            </Button>
            {glRunning && <Progress value={50} className="mt-1 h-1 animate-pulse" />}
            {(glDone || glResult) && !glRunning && (
              <p className="mt-1 text-[11px] text-[#475569]">
                {glResult
                  ? `${glResult.confirmedTerms} term(s) confirmed, ${glResult.normalized} passage(s), ${glResult.terms} replacement(s)` +
                    (glResult.flagged > 0 ? `, ${glResult.flagged} flagged` : "")
                  : "Glossary check completed"}
              </p>
            )}
          </div>
        )}
        {e.grammar_done >= gTotal && gTotal > 0 && !isTranslating && !isVerifying && (
          <div className="mt-2">
            <Button data-testid={`tm-build-${e.lang}`} size="sm" variant="outline"
              disabled={tmBusy || tmRunning}
              onClick={startTmBuild}
              title="Translation memory: groups passages whose SOURCE is identical, picks each group's majority rendering and flags the passages that drifted from it (e.g. the same declarative paragraph rendered 'Hay una sustancia pensante...' in one chapter and 'Existe una sustancia pensante...' in another). On future re-runs of this edition, validated entries are reused instead of calling the translation engine again"
              className="h-8 w-full rounded-lg border-[#e6e4dd] px-2 text-xs hover:bg-slate-900 hover:text-white disabled:opacity-60">
              {tmBusy || tmRunning ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Languages className="mr-1.5 h-3.5 w-3.5" />}
              {tmRunning ? "Building translation memory…" : (tmBuilt ? "Re-build translation memory" : "Build translation memory")}
            </Button>
            {tmBuilt && !tmRunning && (
              <p className="mt-1 text-[11px] text-[#475569]">
                {tmInfo.repeated} repeated group(s), {tmInfo.inconsistent} inconsistent
                {tmInfo.flagged > 0 ? `, ${tmInfo.flagged} flagged` : ""}
                {tmInfo.validated ? " · validated" : " · not validated yet"}
              </p>
            )}
          </div>
        )}
        {e.flagged_count > 0 && (
          <Button data-testid={`apply-all-flags-${e.lang}`} size="sm" variant="outline" disabled={applyAllBusy}
            onClick={applyAllFlags}
            title="Auto-accept every flagged correction for this edition — useful when you don't read the target language yourself"
            className="mt-2 h-8 w-full rounded-lg border-[#e6e4dd] px-2 text-xs hover:bg-slate-900 hover:text-white">
            {applyAllBusy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <CheckCheck className="mr-1.5 h-3.5 w-3.5" />}
            Apply all {e.flagged_count} flagged corrections
          </Button>
        )}
        <Button data-testid={`normalize-titles-${e.lang}`} size="sm" variant="outline" disabled={titlesBusy}
          onClick={normalizeTitles}
          title="Unify chapter/book heading style across the whole edition (each was translated independently, so styles can drift)"
          className="mt-2 h-8 w-full rounded-lg border-[#e6e4dd] px-2 text-xs hover:bg-slate-900 hover:text-white">
          {titlesBusy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <ListOrdered className="mr-1.5 h-3.5 w-3.5" />}
          Normalize chapter titles
        </Button>
        </div>
      </details>
      )}

      <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-[#e6e4dd] pt-4">
        <label className="flex items-center gap-1.5 text-sm text-[#475569] select-none"
          title="Mark this edition as fully reviewed (final read done)">
          <Checkbox data-testid={`reviewed-${e.lang}`} checked={!!e.manually_reviewed} disabled={reviewedBusy}
            onCheckedChange={toggleReviewed} />
          Reviewed
        </label>
        <label className="flex items-center gap-1.5 text-sm text-[#475569] select-none"
          title="Mark whether you've uploaded this edition's EPUB to KDP yourself -- Atlas can't detect this on its own">
          <Checkbox data-testid={`kdp-published-${e.lang}`} checked={!!e.kdp_published} disabled={kdpBusy}
            onCheckedChange={toggleKdpPublished} />
          Published on KDP
        </label>
        {e.review_breakdown?.without_fix > 0 && (
          <button data-testid={`dismiss-notes-${e.lang}`} onClick={dismissNotes} onBlur={() => setConfirmNotes(false)}
            disabled={notesBusy}
            title="Dismiss every open flag that has no correction to apply. A dismissed flag only comes back if its passage changes."
            className={`text-xs ${confirmNotes ? "font-medium text-rose-600" : "text-[#64748b]"} hover:underline disabled:opacity-50`}>
            {notesBusy ? "Dismissing…" : (confirmNotes ? `Confirm: dismiss ${e.review_breakdown.without_fix}` : `Dismiss ${e.review_breakdown.without_fix} notes`)}
          </button>
        )}
        {e.flagged_count > 0 ? (
          <button data-testid={`review-flags-${e.lang}`}
            onClick={() => navigate(`/project/${projectId}/review/${e.id}`)}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-[#e11d48] hover:underline">
            <ShieldAlert className="h-4 w-4" />
            {e.review_breakdown
              ? `${e.review_breakdown.with_fix} to decide · ${e.review_breakdown.without_fix} notes`
              : `${e.flagged_count} to review`}
          </button>
        ) : (
          <span className="text-sm text-[#94a3b8]">{e.epub_ready ? "No issues" : "—"}</span>
        )}
        <div className="flex items-center gap-2">
          <Button data-testid={`preview-epub-${e.lang}`} onClick={() => navigate(`/project/${projectId}/preview/${e.id}`)}
            disabled={!e.epub_ready} size="sm" variant="outline"
            className="rounded-full border-[#e6e4dd] hover:bg-slate-900 hover:text-white disabled:opacity-40">
            <Eye className="mr-1.5 h-4 w-4" /> Preview
          </Button>
          <Button data-testid={`download-epub-${e.lang}`} onClick={download} disabled={!e.epub_ready || busy}
            size="sm" variant="outline"
            className="rounded-full border-[#e6e4dd] hover:bg-slate-900 hover:text-white disabled:opacity-40">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Download className="mr-1.5 h-4 w-4" /> EPUB</>}
          </Button>
        </div>
      </div>
    </motion.div>
  );
}
